import * as ROUTES from 'Routes'
import { useEffect, useRef, useState } from 'react'
import { Form, Formik } from 'formik'
import If from '@mobi/components/If'
import cpfMask from '@mobi/utils/masks/cpf'
import cnpjMask from '@mobi/utils/masks/cnpj'
import analytics from '@mobi/libraries/analytics'
import Icon from 'components/Icon'
import Header from 'components/Header'
import Text from 'components/Text'
import Button from 'components/Button'
import { InputText } from 'components/Fields'
import LandingLayout from '../../layouts/LandingLayout'
import useLandingRegister from './useLandingRegister'
import useMediaQuery from 'hooks/useMediaQuery'
import getMask from 'utils/fields/masks'
import Recaptcha from 'react-google-invisible-recaptcha'
import {
  CAPTCHA_KEY,
  RENEG_CAPTCHA_KEY,
  ROOT_URL,
  ENV,
} from 'gatsby-env-variables'
import removeSpecialChars from '@mobi/utils/formatters/removeSpecialChars'
import DocumentAutoFill from 'components/DocumentAutoFill'
import './LandingRegister.scss'
import WhatsAppButton from 'components/WhatsAppButton'
import ImgStoryblok from 'components/ImgStoryblok'
import DiscountBanner from './DiscountBanner'

const FormAutoCompleteWrapper = ({
  formProps: { setValues },
  document,
  formAutoCompleteValues,
  isSameDocument,
}) => {
  const hasDocument = document?.length

  useEffect(() => {
    if (hasDocument && isSameDocument) {
      setValues({
        document: formAutoCompleteValues?.document,
        name: formAutoCompleteValues?.name,
        email: formAutoCompleteValues?.email,
        cellphone: formAutoCompleteValues?.cellphone,
        birth_date: formAutoCompleteValues?.birth_date,
      })
    }
  }, [document, formAutoCompleteValues])

  return null
}

const LandingRegister = ({ location }) => {
  const documentRef = useRef(null)
  const nameRef = useRef(null)
  const emailRef = useRef(null)
  const dateRef = useRef(null)
  const phoneRef = useRef(null)

  const alreadyHasDocument = !!location?.state?.document

  const {
    onSubmit,
    isCaptchaValid,
    birthdayNotValid,
    disableButton,
    formAutoCompleteValues,
    setBirthdayNotValid,
    setFormUserInfo,
  } = useLandingRegister({
    location,
  })

  const [isCNPJDocument, setIsCNPJDocument] = useState(
    location?.state?.document?.length > 14
  )

  const [isAutoFillFields, setAutoFillFields] = useState(false)

  const isDesktop = useMediaQuery('notMobile')

  const refRecaptchaReneg = useRef(null)

  useEffect(() => {
    if (isAutoFillFields) {
      analytics.track('elementViewed', {
        text: 'register_autocomplete',
      })
    }
  }, [isAutoFillFields])

  const handleTracking = (ev) => {
    const elementClickedPayload = {
      location: ev.currentTarget.dataset.trackingLocation,
      position: ev.currentTarget.dataset.trackingPosition,
      elementType: ev.currentTarget.dataset.trackingElement,
      text: ev.currentTarget.textContent || ev.currentTarget.ariaLabel,
      htmlId: ev.currentTarget.dataset.unique,
      actionOutcome: ev.currentTarget.dataset.trackingActionoutcome,
      outboundUrl: ev.currentTarget.dataset.trackingOutboundurl,
    }

    analytics.track('elementClicked', elementClickedPayload)
  }

  const renderLegalBlok = () => (
    <div className="register-bottom-part__legal-blok">
      <div className="safe-deal-container">
        <figure className="safe-deal-container__image">
          <ImgStoryblok
            src="https://a.storyblok.com/f/119473/x/e99b9cc8ce/safe-deal.svg"
            alt="Acordo seguro"
          />
        </figure>
        <span className="safe-deal-container__text">
          <Text p size={5}>
            Seus dados estão <b>100% seguros na Recovery</b>.
          </Text>
        </span>
      </div>
      <Text p size={11}>
        Ao continuar, autorizo os gestores de banco de dados do cadastro
        positivo a disponibilizarem ao Itaú Unibanco S.A o meu histórico de
        crédito. Estou ciente dos{' '}
        <a
          href="https://www.itau.com.br/seguranca/termo-autorizacao"
          target="_blank"
          rel="noreferrer"
          data-tracking-location="form"
          data-tracking-position="form-register"
          data-tracking-actionoutcome="redirect"
          data-tracking-element="link"
          data-tracking-outboundurl="https://www.itau.com.br/seguranca/termo-autorizacao"
          onClick={handleTracking}
        >
          termos de autorização
        </a>{' '}
        e que poderei efetuar o{' '}
        <a
          href="https://www.itau.com.br/cadastro-positivo"
          target="_blank"
          rel="noreferrer"
          data-tracking-location="form"
          data-tracking-position="form-register"
          data-tracking-actionoutcome="redirect"
          data-tracking-element="link"
          data-tracking-outboundurl="https://www.itau.com.br/cadastro-positivo"
          onClick={handleTracking}
        >
          cancelamento
        </a>{' '}
        a qualquer momento.
      </Text>

      <Text p size={11}>
        Autorizo também a consulta ao{' '}
        <a
          href="https://www.itau.com.br/seguranca/sistema-informacao-credito"
          target="_blank"
          rel="noreferrer"
          data-tracking-location="form"
          data-tracking-position="form-register"
          data-tracking-actionoutcome="redirect"
          data-tracking-element="link"
          data-tracking-outboundurl="https://www.itau.com.br/seguranca/sistema-informacao-credito"
          onClick={handleTracking}
        >
          sistema de informações de crédito
        </a>
        (SCR) do Banco Central, estou ciente que meus dados serão registrados no
        SCR e de acordo com a{' '}
        <a
          href="https://www.itau.com.br/privacidade"
          target="_blank"
          rel="noreferrer"
          data-tracking-location="form"
          data-tracking-position="form-register"
          data-tracking-actionoutcome="redirect"
          data-tracking-element="link"
          data-tracking-outboundurl="https://www.itau.com.br/privacidade"
          onClick={handleTracking}
        >
          política de privacidade.
        </a>
      </Text>
      <Text p size={11}>
        Ao continuar, confirmo que estou ciente e de acordo com os termos de uso
        e com a{' '}
        <a
          href="https://www.itau.com.br/privacidade"
          target="_blank"
          rel="noreferrer"
          data-tracking-location="form"
          data-tracking-position="form-register"
          data-tracking-actionoutcome="redirect"
          data-tracking-element="link"
          data-tracking-outboundurl="https://www.itau.com.br/privacidade"
          onClick={handleTracking}
        >
          política de privacidade.
        </a>
      </Text>
    </div>
  )

  return (
    <LandingLayout>
      <Recaptcha sitekey={CAPTCHA_KEY} />
      <Recaptcha sitekey={RENEG_CAPTCHA_KEY} ref={refRecaptchaReneg} />
      <Header />

      <DiscountBanner />

      <WhatsAppButton dataUnique="7270860c-eded-460e-b7f6-b63bfec2800b" />
      <div className="landing-register-container">
        <div className="landing-register-content">
          <If
            condition={isDesktop}
            renderIf={
              <Text h1 size={4} className="top-text">
                {location?.state?.title ||
                  `Para prosseguir precisamos das informações abaixo`}
              </Text>
            }
            renderElse={
              <If
                condition={location?.state?.title}
                renderIf={
                  <Text h1 size={4} className="top-text">
                    {location?.state?.title}
                  </Text>
                }
                renderElse={
                  <div className="mobile-top-texts">
                    <Text h1 size={6}>
                      Preencha seus dados
                    </Text>
                    <Text h2 size={7}>
                      para calcularmos a melhor oferta
                    </Text>
                  </div>
                }
              />
            }
          />
          <div className="register-form-input">
            <Formik
              initialValues={{
                document: location?.state?.document || '',
                name: '',
                email: '',
                cellphone: '',
                birth_date: '',
              }}
              onSubmit={async (values) => {
                setFormUserInfo(values)

                onSubmit(values, refRecaptchaReneg, {
                  source_url: location.href,
                })
              }}
            >
              {({ isValid, values: { document }, ...formProps }) => {
                const handleEnter = (event, nextInput) => {
                  if (!nextInput || isValid) return

                  if (event.key === 'Enter') {
                    event.preventDefault()

                    nextInput.current.inputElement.focus()
                  }
                }

                const isSameDocumentToAutofill =
                  removeSpecialChars(document) ===
                  (formAutoCompleteValues?.document &&
                    removeSpecialChars(formAutoCompleteValues?.document))

                setAutoFillFields(isSameDocumentToAutofill)

                return (
                  <Form className="register-form-content">
                    {ENV !== 'prod' && (
                      <DocumentAutoFill formProps={formProps} />
                    )}
                    <FormAutoCompleteWrapper
                      formProps={formProps}
                      document={document}
                      formAutoCompleteValues={formAutoCompleteValues}
                      isSameDocument={isSameDocumentToAutofill}
                    />
                    <InputText
                      name="document"
                      label="CPF ou CNPJ"
                      placeholder="000.000.000-00"
                      dataUnique="ddfd903e-1617-4f1d-aed8-ded6b73ab4f0"
                      trackingFieldlabel="CPF ou CNPJ"
                      dataTest="input-document"
                      trackingFieldtype="DOCUMENT"
                      autoFocus={!alreadyHasDocument}
                      ref={documentRef}
                      mask={(e) => {
                        if (e && removeSpecialChars(e).length <= 11)
                          return cpfMask
                        else return cnpjMask
                      }}
                      rules={{ isCpfOrCnpj: true, isRequired: true }}
                      inputMode="numeric"
                      {...formProps}
                      handleChange={(ev) => {
                        const value = removeSpecialChars(ev.target.value)

                        formProps.setFieldValue('document', ev.target.value)
                        setIsCNPJDocument(value.length > 11)
                      }}
                      onKeyDown={(event) => handleEnter(event, nameRef)}
                    />
                    {isSameDocumentToAutofill ? (
                      <Text
                        p
                        size={6}
                        className="register-form-content__autofill-message-text"
                      >
                        Para mais agilidade, preenchemos os campos com os dados
                        do seu último acesso.
                      </Text>
                    ) : null}
                    <InputText
                      name="name"
                      label="Nome completo"
                      placeholder="Nome e sobrenome"
                      rules={{ isName: true, isRequired: true }}
                      dataTest="input-name"
                      dataUnique="97baf47c-178d-4868-a31f-38fe193ec9a3"
                      trackingFieldlabel="Nome completo"
                      trackingFieldtype="FULLNAME"
                      autoFocus={alreadyHasDocument}
                      ref={nameRef}
                      onKeyDown={(event) => handleEnter(event, emailRef)}
                      {...formProps}
                    />

                    {!isCNPJDocument ? (
                      <InputText
                        name="birth_date"
                        label="Data de nascimento"
                        placeholder="00 / 00 / 0000"
                        mask={() => getMask('date')}
                        inputMode="numeric"
                        rules={{ isDate: true, isRequired: true }}
                        dataTest="input-birthdate"
                        dataUnique="9db49595-5c1e-4355-bc28-3965721de06c"
                        trackingFieldlabel="Data de nascimento"
                        trackingFieldtype="DOB"
                        ref={dateRef}
                        {...formProps}
                        handleChange={(ev) => {
                          setBirthdayNotValid('')
                          formProps.handleChange(ev)
                        }}
                        onKeyDown={(event) => handleEnter(event, phoneRef)}
                      />
                    ) : null}
                    <InputText
                      name="email"
                      label="E-mail"
                      placeholder="exemplo@exemplo.com"
                      type="email"
                      rules={{ isEmail: true, isRequired: true }}
                      dataTest="input-email"
                      dataUnique="d9751ae2-d253-4eeb-a083-86f9f543bc46"
                      trackingFieldlabel="E-mail"
                      trackingFieldtype="EMAIL"
                      ref={emailRef}
                      onKeyDown={(event) => handleEnter(event, dateRef)}
                      {...formProps}
                    />
                    <InputText
                      name="cellphone"
                      label="Celular"
                      type="tel"
                      placeholder="(00) 00000-0000"
                      mask={() => getMask('phone')}
                      rules={{ isPhone: true, isRequired: true }}
                      dataTest="input-cellphone"
                      dataUnique="6b5fa8e3-8e17-447d-aef5-0c6ad833b565"
                      trackingFieldlabel="Celular"
                      trackingFieldtype="CONTACT"
                      ref={phoneRef}
                      {...formProps}
                    />
                    {isValid && birthdayNotValid && (
                      <div
                        className="register-form-invalid-container"
                        data-unique="c44fa577-2387-4348-8643-4e8b2adcf684"
                      >
                        <div className="icon">
                          <Icon iconId="iconWarning" />
                        </div>
                        <div className="text">{birthdayNotValid}</div>
                      </div>
                    )}
                    <div className="register-form-button-container">
                      <Button
                        className="button"
                        type="submit"
                        isDisabled={
                          (!isCaptchaValid && !isValid) || disableButton
                        }
                        data-unique="9c06d484-324b-445d-92c5-dec5f8f14285"
                        data-test="button-submit-personal-data"
                        data-tracking-location="form"
                        data-tracking-position="form-register"
                        data-tracking-actionoutcome="redirect"
                        data-tracking-element="button"
                        data-tracking-outboundurl={`${ROOT_URL}${ROUTES.ofertas.path}?step=ofertas`}
                      >
                        Consultar
                      </Button>
                    </div>
                  </Form>
                )
              }}
            </Formik>
            {renderLegalBlok()}
          </div>
        </div>
      </div>
    </LandingLayout>
  )
}

export default LandingRegister
